'use client';
import { useEffect, useLayoutEffect, useState } from 'react';
import dynamic from 'next/dynamic';
import Script from 'next/script';
import { SvgPause } from '@/components/Icons/SvgPause';
import { SvgPlay } from '@/components/Icons/SvgPlay';

// Avoid SSR for ReactPlayer to avoid hydration issues
// https://github.com/cookpete/react-player/issues/1428
const ReactPlayer = dynamic(() => import('react-player/lazy'), { ssr: false });

/**
 * A subcomponent of WayfinderFullFeature that is responsible for looping the
 * video a set number of times.
 */
const LoopCapVideo = ({
  className,
  loopLimit = 3,
  vimeoURL,
  position,
  videoUploadDate,
  videoTitle,
  desktopImage
}) => {
  // Convert to full ISO 8601 format (YYYY-MM-DDTHH:MM:SS+TZ)
  const formattedUploadDate = new Date(videoUploadDate).toISOString();
  const [playNumber, setPlayNumber] = useState(0);
  const [duration, setDuration] = useState(null);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [shouldRenderPlayer, setShouldRenderPlayer] = useState(false);
  const [remountKey, setRemountKey] = useState(0);

  // Bump the key to remount on Activity show, since Vimeo keeps
  // trying to pause itself otherwise. Also autoplay again.
  useLayoutEffect(() => {
    return () => {
      setDuration(null);
      setPlayNumber(0);
      setVideoPlaying(true);
      setRemountKey((key) => key + 1);
    };
  }, []);

  // Mount player only after a meaningful user scroll movement.
  useEffect(() => {
    if (shouldRenderPlayer) return;

    const initialScrollY = window.scrollY;
    const minScrollDelta = 24;

    const onPageScroll = () => {
      if (Math.abs(window.scrollY - initialScrollY) < minScrollDelta) return;
      setShouldRenderPlayer(true);
      setVideoPlaying(true);
      window.removeEventListener('scroll', onPageScroll);
    };

    window.addEventListener('scroll', onPageScroll, { passive: true });

    return () => window.removeEventListener('scroll', onPageScroll);
  }, [shouldRenderPlayer]);

  // I would like to use onEnded instead of onProgress, but onEnded doesn't seem
  // to ever run when we loop (it only runs when the video actually stops).
  const handleProgress = (progress) => {
    if (duration === null) {
      return;
    }

    // If we reach the end of the video minus a threshold, increment the play
    // number, stopping the video once we reach the loop limit.
    if (progress.playedSeconds > duration - 1.5) {
      const nextPlayNumber = playNumber + 1;
      setPlayNumber(nextPlayNumber);
      if (nextPlayNumber >= loopLimit) {
        setVideoPlaying(false);
      }
    }
  };

  const togglePlay = () =>
    setVideoPlaying((isPlaying) => {
      // If we're the ones starting the video, reset the play number
      if (!isPlaying) {
        setPlayNumber(0);
      }
      return !isPlaying;
    });

  return (
    <>
      <div id={`full-feature-video-${position}`} className={className}>
        {shouldRenderPlayer ? (
          <>
            <ReactPlayer
              key={remountKey}
              url={vimeoURL}
              loop={false}
              playing={videoPlaying}
              playsinline={true}
              volume={0}
              muted={true}
              width="100%"
              height="100%"
              progressInterval={1000}
              onDuration={(duration) => setDuration(duration)}
              onProgress={handleProgress}
              className="absolute top-0 left-0"
            />
            {videoPlaying ? (
              <SvgPause
                handleClick={togglePlay}
                className="right-[20px] bottom-[20px] h-[40px] w-[40px] sm:right-[21px] sm:bottom-[18px]"
              />
            ) : (
              <SvgPlay
                handleClick={togglePlay}
                className="right-[20px] bottom-[20px] h-[40px] w-[40px] sm:right-[21px] sm:bottom-[18px]"
              />
            )}
          </>
        ) : (
          <div
            aria-hidden="true"
            className="pointer-events-none h-full w-full opacity-0"
          />
        )}
      </div>

      {vimeoURL && (
        <Script
          id={videoTitle}
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: `{
            "@context": "https://schema.org/",
            "@type": "VideoObject",
            "name": "${videoTitle}",
            "thumbnailUrl": "${desktopImage.url}",
            "uploadDate": "${formattedUploadDate}",
            "embedUrl": "${vimeoURL}"
            }`
          }}
        ></Script>
      )}
    </>
  );
};

export default LoopCapVideo;
